// ==================== APPOINTMENT ACTION PAGE ====================
/**
 * SAFE PATCH: Página para cliente confirmar/reagendar/cancelar via link do email.
 * Rota: /client/appointment/:id/:action
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Calendar, X, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AppointmentAction() {
  const { id, action } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading, success, error, confirm
  const [message, setMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Verificar se está logado
    const token = localStorage.getItem('token');
    if (!token) {
      // Salvar a ação pendente e redirecionar para login
      sessionStorage.setItem('pending_reminder_action', JSON.stringify({ id, action }));
      navigate('/client/login', { 
        state: { 
          message: 'Faça login para confirmar seu agendamento',
          returnTo: `/client/appointment/${id}/${action}`
        }
      });
      return;
    }

    // Se ação válida, mostrar tela de confirmação
    if (['confirm', 'reschedule', 'cancel'].includes(action)) {
      setStatus('confirm');
    } else {
      setStatus('error');
      setMessage('Ação inválida');
    }
  }, [id, action, navigate]);

  const handleAction = async () => {
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reminders/${id}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || getSuccessMessage(action));
      } else {
        setStatus('error');
        setMessage(data.detail || 'Erro ao processar sua solicitação');
      }
    } catch (error) {
      console.error('Erro:', error);
      setStatus('error');
      setMessage('Erro de conexão. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  const getSuccessMessage = (action) => {
    switch (action) {
      case 'confirm':
        return 'Presença confirmada! O prestador foi notificado.';
      case 'reschedule':
        return 'Solicitação de reagendamento enviada. O prestador entrará em contato.';
      case 'cancel':
        return 'Solicitação de cancelamento enviada.';
      default:
        return 'Ação processada com sucesso!';
    }
  };

  const getActionConfig = (action) => {
    switch (action) {
      case 'confirm':
        return {
          title: 'Confirmar Presença',
          description: 'Você está confirmando que comparecerá ao agendamento.',
          icon: Check,
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          buttonText: 'Sim, confirmar presença',
          buttonClass: 'bg-green-600 hover:bg-green-700'
        };
      case 'reschedule':
        return {
          title: 'Solicitar Reagendamento',
          description: 'Você está solicitando reagendar este serviço. O prestador entrará em contato.',
          icon: Calendar,
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          buttonText: 'Sim, quero reagendar',
          buttonClass: 'bg-amber-600 hover:bg-amber-700'
        };
      case 'cancel':
        return {
          title: 'Solicitar Cancelamento',
          description: 'Você está solicitando cancelar este agendamento.',
          icon: X,
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          buttonText: 'Sim, cancelar agendamento',
          buttonClass: 'bg-red-600 hover:bg-red-700'
        };
      default:
        return null;
    }
  };

  const config = getActionConfig(action);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Tudo certo!</h1>
          <p className="text-slate-600 mb-6">{message}</p>
          <Button
            onClick={() => navigate('/client/dashboard')}
            className="w-full"
            data-testid="go-to-dashboard-btn"
          >
            Ir para o Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Ops!</h1>
          <p className="text-slate-600 mb-6">{message}</p>
          <Button
            onClick={() => navigate('/client/dashboard')}
            variant="outline"
            className="w-full"
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Confirm state
  if (status === 'confirm' && config) {
    const Icon = config.icon;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
          {/* Header */}
          <button
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Voltar</span>
          </button>

          {/* Icon */}
          <div className={`w-16 h-16 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${config.iconColor}`} />
          </div>

          {/* Content */}
          <h1 className="text-xl font-bold text-slate-800 text-center mb-2">
            {config.title}
          </h1>
          <p className="text-slate-600 text-center mb-8">
            {config.description}
          </p>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleAction}
              disabled={processing}
              className={`w-full ${config.buttonClass}`}
              data-testid={`confirm-action-${action}`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Processando...
                </>
              ) : (
                config.buttonText
              )}
            </Button>
            
            <Button
              onClick={() => navigate('/client/dashboard')}
              variant="ghost"
              disabled={processing}
              className="w-full"
            >
              Não, voltar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
